/**
 * Geographic Utility Functions
 *
 * Pure utility functions for geographic calculations.
 * No external dependencies required.
 */

/**
 * Calculate the distance between two geographic points in miles
 * Uses the Haversine formula
 *
 * @param {number} lat1 - Latitude of first point
 * @param {number} lon1 - Longitude of first point
 * @param {number} lat2 - Latitude of second point
 * @param {number} lon2 - Longitude of second point
 * @returns {number} Distance in miles
 */
export function getDistance(lat1, lon1, lat2, lon2) {
	// Convert degrees to radians
	const latRad1 = (lat1 * Math.PI) / 180;
	const lonRad1 = (lon1 * Math.PI) / 180;
	const latRad2 = (lat2 * Math.PI) / 180;
	const lonRad2 = (lon2 * Math.PI) / 180;

	// Radius of Earth in miles
	const R = 3959;

	const d =
		Math.acos(
			Math.sin(latRad1) * Math.sin(latRad2) +
				Math.cos(latRad1) *
					Math.cos(latRad2) *
					Math.cos(lonRad2 - lonRad1)
		) * R;

	return Math.abs(d);
}

/**
 * Convert location objects to Google Maps LatLngBounds
 *
 * @param {Object} google - Google Maps API object
 * @param {Array<{latitude: number, longitude: number}>} locations - Array of location objects
 * @returns {google.maps.LatLngBounds} Bounds object
 */
export function locationsToBounds(google, locations) {
	return locations.reduce((bound, location) => {
		const point = new google.maps.LatLng(
			location.latitude,
			location.longitude
		);
		bound.extend(point);
		return bound;
	}, new google.maps.LatLngBounds());
}

/**
 * Format address by removing US country suffix
 * @param {string} address - Address string
 * @returns {string} Formatted address
 */
export function formatAddress(address) {
	if (address.substring(address.length - 5) === ", USA") {
		return address.substring(0, address.length - 5);
	}
	if (address.substring(address.length - 15) === ", United States") {
		return address.substring(0, address.length - 15);
	}
	return address;
}
